import { Button } from '@ai-tutor/ui';
import { BookOpen, ListTree } from 'lucide-react';
import type { SourceItem } from '@/lib/api-client';
import {
  fileLimit,
  scopeUnitOf,
  unitCount,
} from '@/lib/sources/scopeSelection';

interface LessonScopeCheckpointProps {
  /** The AWAITING_SCOPE source the student needs to choose parts of. */
  source: SourceItem;
  /** Total large sources still awaiting scope (>=1). >1 shows a queue hint —
   *  we scope them one at a time, this one first. */
  pendingCount?: number;
  /** Open the source picker. */
  onChoose?: () => void;
}

/**
 * Center-panel state for a source that is waiting on the student to choose
 * which parts to use - because it is over its per-file limit or would go past
 * the month's pages. Works for every kind of file (pages, slides, sheets).
 * This is deliberately a calm *checkpoint*, not a loading spinner: nothing is
 * processing, we're waiting on a decision. The single next action reopens the
 * picker.
 */
/** Heading and explanation, in the source's own unit and for why it waits. */
function checkpointCopy(source: SourceItem) {
  const unit = scopeUnitOf(source);
  const total =
    source.size_units ??
    source.outline?.total_units ??
    source.page_count ??
    null;
  const limit = fileLimit(source, unit);
  const monthly = source.scope_reason === 'over_monthly_allowance';
  const size =
    total === null
      ? ''
      : `has ${source.size_estimated ? 'about ' : ''}${unitCount(unit, total)}. `;
  let why = 'Pick the parts you need.';
  if (monthly) {
    why =
      "It's more than you have left this month, so pick the parts you need.";
  } else if (limit !== null) {
    why = `Your plan reads up to ${unitCount(unit, limit)} per file, so pick the parts you need.`;
  }
  return {
    unit,
    title: monthly
      ? 'Choose what to use from this file'
      : "This file is large, let's focus it",
    body: size + why,
  };
}

export function LessonScopeCheckpoint({
  source,
  pendingCount = 1,
  onChoose,
}: LessonScopeCheckpointProps) {
  const { unit, title, body } = checkpointCopy(source);
  const parts = (source.outline?.sections ?? []).filter(s => !s.is_matter);
  const preview = parts
    .slice(0, 3)
    .map(s => s.title)
    .join(', ');
  const noun = unit === 'cell' ? 'sheet' : 'part';

  return (
    <div className='flex h-full flex-col items-center justify-center px-6 text-center'>
      <div className='flex h-14 w-14 items-center justify-center rounded-full bg-primary/10'>
        <BookOpen className='h-6 w-6 text-primary' />
      </div>

      <h2 className='mt-5 text-xl font-semibold text-foreground'>{title}</h2>

      <p className='mt-2 max-w-md text-sm leading-relaxed text-muted-foreground'>
        <span className='font-medium text-foreground'>{source.name}</span>{' '}
        {body}
      </p>

      {parts.length > 0 && (
        <p className='mt-4 max-w-md text-xs text-muted-foreground'>
          {parts.length} {noun}
          {parts.length === 1 ? '' : 's'} found
          {preview ? ` · ${preview}` : ''}
          {parts.length > 3 ? '…' : ''}
        </p>
      )}

      <Button
        onClick={onChoose}
        className='mt-6 h-11 w-full gap-2 sm:h-9 sm:w-auto'
      >
        <ListTree className='h-4 w-4' />
        Choose what to use
      </Button>

      <p className='mt-3 text-xs text-muted-foreground'>
        {pendingCount > 1
          ? `${pendingCount} files need a choice. You'll do each one, this one first.`
          : 'Only what you choose gets processed. The rest stays listed.'}
      </p>
    </div>
  );
}
